/**
 * Auto-scroll engine.
 *
 * Runs on requestAnimationFrame with a fractional position accumulator, in two modes:
 *
 *   constant — a fixed px/s, one pixel every `speed` ms, the same rate the speed levels have
 *              always meant. (It used to be a setInterval writing whole pixels, which at level 1
 *              is one pixel every 150ms and visibly steps; the accumulator smooths that out.)
 *
 *   smart    — follows the schedule built in smartScrollSchedule.js, so the velocity varies through
 *              the song: a repeated chorus crawls, a dense bridge hurries, and the page holds near
 *              the top while the intro plays. The player's pace multiplier scales the song clock
 *              rather than the schedule, and only over the parts the schedule had to estimate —
 *              see the rate in scrollFrame.
 *
 * startScroll keeps its original signature, so all three call sites (AutoScrollPlugin, SuperScroll
 * and ViewBody's double-tap) get both modes without knowing which is running.
 *
 * Writing an absolute position every frame means the engine has to know when to let go. A wheel
 * notch, a trackpad glide and a phone flick are all scroll animations the browser runs itself, and
 * assigning scrollTop in the middle of one cancels it — so a player nudging the page to read a line
 * under the plugin bar would find it snapping back sixty times a second. The gate below hands the
 * page over the moment the user touches it and takes it back once everything has been still for a
 * moment.
 */

import { DEFAULT_PACE } from './autoScrollConstants';
import { getSchedule } from './smartScrollSchedule';
import { getSetting, settingKeys } from './settings';

// A gap larger than this means the loop was suspended (a background tab, a paused debugger).
// Advancing by the real elapsed time would lurch the page, so the frame is treated as a short one.
const MAX_FRAME_SECONDS = 0.25;

// How long everything has to be still — finger, momentum, wheel animation — before the engine
// starts writing again. Short enough to feel like it never stopped, long enough that it isn't
// grabbing at a scroll the browser is still animating.
const RESUME_DELAY_MS = 400;

// How far the page may move without us before we call it the user's doing. Our own writes read
// back exactly; this only has to survive sub-pixel rounding and the browser's own scroll anchoring.
const DRIFT_TOLERANCE = 2;

// A manual move smaller than this fraction of the screen is a reading correction — the player
// pushing a covered line into view. Anything larger is them going to a different part of the song.
const NUDGE_FRACTION = 1 / 3;

// The keys that scroll the page. A keystroke that isn't one of these has nothing to do with us.
const SCROLL_KEYS = ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ', 'Spacebar'];

let currentY = 0;
let scheduleTime = 0;
let lastTimestamp = 0;
let lastAppliedTop = 0;
let pace = DEFAULT_PACE;
let smartEnabled = false;

// How far the page has been shifted from the schedule's own path by the player's corrections. The
// schedule says where the song is; this says where they are reading it from.
let scrollOffset = 0;
// Identity of the schedule the offset was measured against — a rebuild is new geometry, and an
// offset in the old layout means nothing in the new one.
let lastSchedule = null;

let yieldUntil = 0;
let touchActive = false;
let wasYielding = false;

/**
 * The pace multiplier over the estimated parts of the smart schedule. Applied live, so a nudge
 * mid-song takes effect on the next frame rather than restarting the scroll.
 */
export function setScrollPace(nextPace) {
  pace = nextPace > 0 ? nextPace : DEFAULT_PACE;
}

export function getScrollPace() {
  return pace;
}

/** Where the scroll has reached in the song, in schedule seconds. Read by the trend indicator. */
export function getScheduleTime() {
  return scheduleTime;
}

/** The part the song clock is in, `{ partId, songId }`. Null in constant mode, which has no parts. */
export function getPlayingPart() {
  const schedule = smartEnabled ? getSchedule() : null;

  return schedule ? schedule.partAt(scheduleTime) : null;
}

/* ---------------------------------------------------------------------------
 * The yield gate
 * ------------------------------------------------------------------------- */

/** Hands the page to the user for a moment. Every further move pushes the moment back. */
const yieldPage = () => {
  yieldUntil = performance.now() + RESUME_DELAY_MS;
};

// A touch on its own is not a scroll — it's a tap on a chord, or on the pace button. Only the
// finger moving hands the page over; the flag exists so a slow drag that pauses mid-way doesn't
// let the engine back in while the finger is still down.
const onTouchStart = () => {
  touchActive = true;
};
const onTouchEnd = () => {
  touchActive = false;
};
const onTouchMove = () => {
  touchActive = true;
  yieldPage();
};
const onKeyDown = (event) => {
  if (SCROLL_KEYS.includes(event.key)) yieldPage();
};

const INPUT_EVENTS = [
  ['wheel', yieldPage],
  ['touchstart', onTouchStart],
  ['touchmove', onTouchMove],
  ['touchend', onTouchEnd],
  ['touchcancel', onTouchEnd],
  ['keydown', onKeyDown],
];

function attachInputListeners() {
  INPUT_EVENTS.forEach(([type, handler]) => window.addEventListener(type, handler, { passive: true }));
}

function detachInputListeners() {
  INPUT_EVENTS.forEach(([type, handler]) => window.removeEventListener(type, handler));
  touchActive = false;
  yieldUntil = 0;
}

/**
 * Starts auto-scrolling.
 * @param {Object} scrollTimerRef - React ref holding the animation frame id
 * @param {Function} setIsScrolling - State setter for scrolling status
 * @param {Function} setScrollingSpeed - State setter for scroll speed
 * @param {number} speed - Milliseconds per pixel, used in constant mode
 * @param {Function} onScrollEnd - Callback when scrolling reaches the end
 */
export function startScroll(scrollTimerRef, setIsScrolling, setScrollingSpeed, speed, onScrollEnd) {
  stopScroll(scrollTimerRef, setIsScrolling);

  const doc = document.documentElement;

  smartEnabled = Boolean(getSetting(settingKeys.smartScroll));
  currentY = doc.scrollTop;
  lastAppliedTop = doc.scrollTop;
  lastTimestamp = 0;
  scrollOffset = 0;
  wasYielding = false;

  const schedule = smartEnabled ? getSchedule() : null;
  lastSchedule = schedule;
  // Resume where the page already is rather than from the top, so starting mid-song — or restarting
  // after a manual scroll — picks up at the right point in the music
  scheduleTime = schedule ? schedule.timeAt(doc.scrollTop) : 0;

  attachInputListeners();

  const step = (timestamp) => {
    scrollFrame(timestamp, speed, onScrollEnd);
    if (scrollTimerRef.current !== null) {
      scrollTimerRef.current = requestAnimationFrame(step);
    }
  };

  scrollTimerRef.current = requestAnimationFrame(step);

  setIsScrolling(true);
  setScrollingSpeed(speed);
}

/**
 * Stops auto-scrolling
 * @param {Object} scrollTimerRef - React ref holding the animation frame id
 * @param {Function} setIsScrolling - State setter for scrolling status
 */
export function stopScroll(scrollTimerRef, setIsScrolling) {
  if (scrollTimerRef.current !== null && scrollTimerRef.current !== undefined) {
    cancelAnimationFrame(scrollTimerRef.current);
    scrollTimerRef.current = null;
  }
  detachInputListeners();
  setIsScrolling(false);
}

/**
 * One frame of scrolling.
 * @private
 */
function scrollFrame(timestamp, speed, onScrollEnd) {
  const doc = document.documentElement;
  const height = doc.scrollHeight - doc.clientHeight;
  const clampY = (y) => Math.min(Math.max(y, 0), Math.max(height, 0));

  if (!lastTimestamp) {
    lastTimestamp = timestamp;
    return;
  }

  const elapsed = Math.min((timestamp - lastTimestamp) / 1000, MAX_FRAME_SECONDS);
  lastTimestamp = timestamp;

  // Read the schedule every frame rather than capturing it: a rotation or a font-size change
  // rebuilds it mid-song, and picking up the new one while keeping scheduleTime is what lets the
  // scroll keep its place in the music across a re-measure.
  const schedule = smartEnabled ? getSchedule() : null;
  if (schedule !== lastSchedule) {
    lastSchedule = schedule;
    scrollOffset = 0;
  }

  // The page moving without us is the user too, whether or not it came with an event we hear —
  // a scrollbar drag, or the momentum still running after a flick.
  if (Math.abs(doc.scrollTop - lastAppliedTop) > DRIFT_TOLERANCE) yieldPage();

  if (touchActive || timestamp < yieldUntil) {
    // Hands off: not a pixel written, and the song clock held where it was, so letting go resumes
    // from the same moment in the music rather than from wherever it would have run to meanwhile.
    lastAppliedTop = doc.scrollTop;
    wasYielding = true;
    return;
  }

  if (wasYielding) {
    wasYielding = false;
    reanchor(doc, schedule, clampY);
  }

  if (schedule) {
    // The pace multiplier is the player's correction to an estimate, so it only applies where
    // there was one. A part whose length the chart states — bar lines, or a stopwatched directive
    // — plays at that length whatever the slider says, and the clock runs through it at 1x.
    // Sampled per frame rather than per part: a frame is 16ms and the boundary is not worth
    // splitting, and reading it off the clock keeps this in step with schedule.scrollAt below.
    scheduleTime += elapsed * (schedule.measuredAt?.(scheduleTime) ? 1 : pace);
    currentY = clampY(schedule.scrollAt(scheduleTime) + scrollOffset);
  } else {
    currentY += (1000 / speed) * elapsed;
  }

  // Only when the pixel actually changes. At the slowest level a frame advances a tenth of a pixel,
  // and writing the same value back nine frames out of ten is both the 1px flutter and nine more
  // chances to cancel a scroll the browser is animating.
  const target = Math.round(currentY);
  if (target !== lastAppliedTop) {
    doc.scrollTop = target;
    lastAppliedTop = doc.scrollTop;
  }

  const reachedEnd = Math.ceil(doc.scrollTop) >= height || isFooterInViewport();
  const scheduleFinished = schedule ? scheduleTime >= schedule.duration : false;

  if (reachedEnd || scheduleFinished) {
    if (onScrollEnd) onScrollEnd();
  }
}

/**
 * Picks the scroll back up where the user left it.
 *
 * A small move is a reading correction — a line was under the plugin bar — so the song keeps its
 * place and its pace and the whole path shifts by however far they pushed it. A large one is them
 * going somewhere else in the song, so smart mode re-times to whatever part they landed on.
 * @private
 */
function reanchor(doc, schedule, clampY) {
  if (!schedule) {
    // A constant scroll has no path to preserve, only a position
    currentY = doc.scrollTop;
    return;
  }

  const expected = clampY(schedule.scrollAt(scheduleTime) + scrollOffset);
  const delta = doc.scrollTop - expected;

  if (Math.abs(delta) <= doc.clientHeight * NUDGE_FRACTION) {
    scrollOffset += delta;
  } else {
    scrollOffset = 0;
    scheduleTime = schedule.timeAt(doc.scrollTop);
  }

  currentY = clampY(schedule.scrollAt(scheduleTime) + scrollOffset);
}

/**
 * Checks if the footer-end element is in viewport
 * Internal helper function used to detect when scrolling should stop.
 * @private
 * @returns {boolean}
 */
function isFooterInViewport() {
  const element = document.querySelector('.footer-end');
  if (!element) return false;

  const rect = element.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
}
